import { useMutation, useQuery } from '@apollo/client';
import CancelIcon from '@mui/icons-material/Cancel';
import { Box, Button, Grid, InputBase, useTheme } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import { useFormik } from 'formik';
import React, { type ChangeEvent, type Dispatch, type SetStateAction, useState } from 'react';
import { type DragSourceMonitor, type DropTargetMonitor, useDrag, useDrop } from 'react-dnd';
import { Title } from 'src/Administration/Common';
import allFieldDefinitionsQuery from 'src/api/admin/allFieldDefinitionsQuery';
import createViewFieldMutation from 'src/api/admin/createViewFieldMutation';
import deleteViewFieldMutation from 'src/api/admin/deleteViewFieldMutation';
import updateViewFieldMutation from 'src/api/admin/updateViewFieldMutation';
import allViews from 'src/api/viewModels/allViews';
import FILTER_CATEGORIES_TAB_IMAGE from 'src/assets/viewsImages/filterCategoriesTabImage.png';
import ImageHover from 'src/components/ImageHover/ImageHover';
import { useCustomToast } from 'src/hooks/menu';
import * as Yup from 'yup';
import type { AllFieldDefsData, FieldDefinition } from '../../types';
import styles from './FilterCategoriesTabStyles';

const GRID_CONTAINER_SPACING = 4;

interface ViewFieldData {
  id: string;
  name: string;
  field: FieldDefinition;
}

interface ViewFieldsEdge {
  node: ViewFieldData;
}

interface ViewNode {
  id: string;
  viewfieldSet: {
    edges: ViewFieldsEdge[];
  };
}

interface AllViewsEdge {
  node: ViewNode;
}

interface AllViewsData {
  allViews: {
    edges: AllViewsEdge[];
  };
}

interface ActiveField {
  id?: string;
  name?: string;
  field: FieldDefinition;
}

interface DragItem {
  type: string;
  id: string;
  index: number;
}

interface FieldsListSearchProps {
  allAvailableFieldsData: FieldDefinition[];
  onSelect: (field: ActiveField) => void;
  activeFields: ActiveField[];
}

interface FieldsListSearchItemProps {
  a: FieldDefinition;
  index: number;
  onSelect: (field: ActiveField) => void;
  handleDrop1: (node: FieldDefinition) => void;
}

interface SelectedFieldsProps {
  activeFields: ActiveField[];
  setActiveFields: Dispatch<SetStateAction<ActiveField[]>>;
  onRemove: (field: ActiveField) => void;
}

interface SelectedFieldsItemsProps {
  onRemove: (field: ActiveField) => void;
  field: ActiveField;
  index: number;
  handleDrop2: (dragIndex: number, hoverIndex: number) => void;
}

export default function FilterCategoriesTabComponent(): React.ReactElement | null {
  const { data: allAvailableFields } = useQuery<AllFieldDefsData>(allFieldDefinitionsQuery, {
    fetchPolicy: 'cache-and-network',
  });
  const { data: FilterBarFirstDropdownViewData } = useQuery<AllViewsData>(allViews, {
    fetchPolicy: 'network-only',
    variables: {
      viewName: 'Library',
      componentName: 'FilterBarFirstDropdown',
    },
  });

  if (!allAvailableFields) return null;
  if (!FilterBarFirstDropdownViewData) return null;

  return (
    <FilterCategoriesTab
      allAvailableFieldsData={allAvailableFields?.allFieldDefs}
      FilterBarFirstDropdownViewData={FilterBarFirstDropdownViewData?.allViews}
    />
  );
}

interface FilterCategoriesTabProps {
  allAvailableFieldsData: AllFieldDefsData['allFieldDefs'];
  FilterBarFirstDropdownViewData: AllViewsData['allViews'];
}

function FilterCategoriesTab({
  allAvailableFieldsData,
  FilterBarFirstDropdownViewData,
}: FilterCategoriesTabProps): React.ReactElement {
  const { toastr } = useCustomToast();
  const classes = styles();
  const viewId = FilterBarFirstDropdownViewData?.edges[0]?.node?.id;

  const originalActiveFields: ActiveField[] =
    FilterBarFirstDropdownViewData?.edges[0]?.node?.viewfieldSet?.edges.map((a) => ({
      ...a.node,
    })) || [];

  const [currentActiveFields, setCurrentActiveField] = useState<ActiveField[]>(originalActiveFields);
  const [createFields] = useMutation(createViewFieldMutation);
  const [updateFields] = useMutation(updateViewFieldMutation);
  const [deleteFields] = useMutation(deleteViewFieldMutation);

  const allAvailableFields: FieldDefinition[] = allAvailableFieldsData?.edges
    .map((a) => a.node)
    .filter((a) => !currentActiveFields?.find((b) => b.field.id === a.id));

  const duplicateObjectOfActiveAndDefault = (activeFields: ActiveField, defaultActiveFields: ActiveField): boolean =>
    activeFields.id === defaultActiveFields.id;

  const onlyInLeft = <T,>(left: T[], right: T[], compareFunction: (a: T, b: T) => boolean): T[] =>
    left.filter((leftValue) => !right.some((rightValue) => compareFunction(leftValue, rightValue)));

  const toBeDeletedFields = onlyInLeft(originalActiveFields, currentActiveFields, duplicateObjectOfActiveAndDefault);

  const createUpdateFieldsOnSubmit = (): void => {
    for (const [sequence, { field, id, name }] of currentActiveFields.entries()) {
      if (id) {
        updateFields({
          variables: {
            viewId,
            viewFieldId: id,
            fieldDefId: field.id,
            sequence,
            fieldLabel: name,
            fieldType: field.fieldType,
          },
        }).catch(() => {
          // Silent catch
        });
        continue;
      }
      createFields({
        variables: {
          viewId,
          fieldDefId: field.id,
          fieldLabel: name,
          sequence,
          fieldType: field.fieldType,
          autoSequence: false,
        },
      });
    }
  };

  const formik = useFormik({
    initialValues: {},
    validationSchema: Yup.object({}),
    onSubmit: async () => {
      createUpdateFieldsOnSubmit();

      for (const { id } of toBeDeletedFields) {
        if (id) {
          deleteFields({
            variables: {
              viewFieldId: id,
            },
          })
            .then(() => {
              // Silent success
            })
            .catch(() => {
              // Silent catch
            });
        }
      }
      toastr.success('Filter categories updated successfully');
    },
  });

  const onClickReset = (): void => {
    setCurrentActiveField(originalActiveFields);
  };

  const addToActiveFields = (field: ActiveField): void => {
    setCurrentActiveField((v) => [...v, field]);
  };

  const removeFromActiveFields = (e: ActiveField): void => {
    setCurrentActiveField((v) => v.filter(({ field }) => field.id !== e.field.id));
  };

  return (
    <Box
      sx={{
        width: '95%',
        margin: '0 auto',
        marginTop: '30px',
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={GRID_CONTAINER_SPACING}>
          <Grid item xs={12}>
            <Title>Filter Categories</Title>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={GRID_CONTAINER_SPACING}
          style={{
            marginBottom: '30px',
          }}
        >
          <Grid item xs={4}>
            <div className={classes.cardTitle}>Available Fields</div>
            <FieldsListSearch
              allAvailableFieldsData={allAvailableFields}
              onSelect={addToActiveFields}
              activeFields={currentActiveFields}
            />
          </Grid>
          <Grid item xs={4}>
            <div className={classes.cardTitle}>Active Fields</div>
            <SelectedFields
              onRemove={removeFromActiveFields}
              activeFields={currentActiveFields}
              setActiveFields={setCurrentActiveField}
            />
          </Grid>
          <Grid item xs={4}>
            <div className={classes.enlargeTitleImage}>
              <div className={classes.enlargeImage}>
                <ImageHover
                  enlargeImageUrl={FILTER_CATEGORIES_TAB_IMAGE}
                  altText="Filter Categories Tab Image"
                  defaultWidth={290}
                  defaultHeight={330}
                />
              </div>
            </div>
          </Grid>
        </Grid>

        <Grid container spacing={GRID_CONTAINER_SPACING}>
          <Grid className={classes.cancelSaveButtons} item xs={12}>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={Object.keys(formik.errors).length > 0}
              style={{
                marginRight: '20px',
              }}
            >
              Save
            </Button>
            <Button onClick={onClickReset} color="secondary">
              Reset
            </Button>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
}

const SelectedFields: React.FC<SelectedFieldsProps> = ({ activeFields, setActiveFields, onRemove }) => {
  const classes = styles();
  const theme = useTheme();

  const handleDrop2 = (dragIndex2: number, hoverIndex2: number): void => {
    const draggedItem2 = activeFields[dragIndex2];
    setActiveFields((prevItems2) => {
      const updatedItems2 = [...prevItems2];
      updatedItems2.splice(dragIndex2, 1);
      updatedItems2.splice(hoverIndex2, 0, draggedItem2);
      return updatedItems2;
    });
  };

  const [{ isOver: isOver1 }, dropTarget1] = useDrop<DragItem, void, { isOver: boolean }>({
    accept: 'FILTER_CATEGORIES_AVAILABLE_TO_ACTIVE',
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  return (
    <div
      ref={(node) => {
        dropTarget1(node);
      }}
      className={classes.fieldContainer}
      style={{
        padding: '30px 15px',
        backgroundColor: isOver1 ? theme.palette.grey[400] : theme.palette.grey[500],
      }}
    >
      <div className={classes.fieldListContainer}>
        {activeFields.map((field, index) => (
          <SelectedFieldsItems
            key={field.id || `field-${index}`}
            onRemove={onRemove}
            field={field}
            index={index}
            handleDrop2={handleDrop2}
          />
        ))}
      </div>
    </div>
  );
};

const SelectedFieldsItems: React.FC<SelectedFieldsItemsProps> = ({ onRemove, field, index, handleDrop2 }) => {
  const classes = styles();
  const theme = useTheme();
  const id = field.id || '';

  const [{ isOver: isOver2 }, dropTarget2] = useDrop<DragItem, void, { isOver: boolean }>({
    accept: 'FILTER_CATEGORIES_ACTIVE_TO_ACTIVE',
    collect: (monitor: DropTargetMonitor) => ({
      isOver: monitor.isOver(),
    }),
    drop(item: DragItem) {
      const dragIndex2 = item.index;
      const hoverIndex2 = index;
      if (dragIndex2 === hoverIndex2) {
        return;
      }
      handleDrop2(dragIndex2, hoverIndex2);
      item.index = hoverIndex2;
    },
  });

  const [{ isDragging: isDragging2 }, dragTarget2] = useDrag<DragItem, void, { isDragging: boolean }>({
    type: 'FILTER_CATEGORIES_ACTIVE_TO_ACTIVE',
    item: { type: 'FILTER_CATEGORIES_ACTIVE_TO_ACTIVE', id, index },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
  });

  const onClickField = (node: ActiveField): void => {
    onRemove(node);
  };

  return (
    <div
      ref={(node) => {
        dropTarget2(node);
      }}
      className={classes.addedContentItem}
    >
      <div
        ref={(node) => {
          dragTarget2(node);
        }}
      >
        {field.name || field?.field?.name}
      </div>
      <button
        type="button"
        onClick={() => onClickField(field)}
        style={{
          marginRight: '10px',
          display: 'flex',
          alignItems: 'center',
          opacity: isDragging2 ? 0.5 : 1,
          backgroundColor: isOver2 ? theme.palette.grey[400] : theme.palette.grey[500],
          border: 'none',
          padding: 0,
          cursor: 'pointer',
        }}
      >
        <CancelIcon className={classes.cancelIcon} fontSize={'inherit'} />
      </button>
    </div>
  );
};

const FieldsListSearch: React.FC<FieldsListSearchProps> = ({ allAvailableFieldsData, activeFields, onSelect }) => {
  const classes = styles();
  const [searchVal, setSearchVal] = useState<string>('');

  const onChangeSearch = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearchVal(e.target.value);
  };

  const handleDrop1 = (node: FieldDefinition): void => {
    onSelect({ field: node });
  };

  return (
    <div className={classes.fieldContainer}>
      <div className={classes.fieldSearchBox}>
        <InputBase
          className={classes.searchInput}
          placeholder={'Quick Search'}
          value={searchVal}
          onChange={onChangeSearch}
        />
      </div>
      <div className={classes.fieldListContainer}>
        {allAvailableFieldsData
          .filter((a) => !activeFields.find((b) => b.id === a.id))
          .filter((a) => a.name.toUpperCase().includes(searchVal.toUpperCase()))
          .map((a, index) => (
            <FieldsListSearchItem key={a.id} index={index} onSelect={onSelect} a={a} handleDrop1={handleDrop1} />
          ))}
      </div>
    </div>
  );
};

const FieldsListSearchItem: React.FC<FieldsListSearchItemProps> = ({ a, index, onSelect, handleDrop1 }) => {
  const classes = styles();

  const onClickField = (node: FieldDefinition): void => {
    onSelect({ field: node });
  };

  const id = a.id;
  const [{ isDragging: isDragging1 }, dragTarget1] = useDrag<DragItem, void, { isDragging: boolean }>({
    type: 'FILTER_CATEGORIES_AVAILABLE_TO_ACTIVE',
    item: { type: 'FILTER_CATEGORIES_AVAILABLE_TO_ACTIVE', id, index },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: !!monitor.isDragging(),
    }),
    end: (_dragItem: DragItem, monitor: DragSourceMonitor) => {
      if (monitor.didDrop()) {
        handleDrop1(a);
      }
    },
  });

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: Drag-and-drop item also supports click selection; complex interaction pattern
    // biome-ignore lint/a11y/useKeyWithClickEvents: Drag-and-drop item also supports click selection; complex interaction pattern
    <div
      ref={(node) => {
        dragTarget1(node);
      }}
      style={{
        opacity: isDragging1 ? 0.5 : 1,
      }}
      className={classes.fieldListItem}
      onClick={() => onClickField(a)}
    >
      <Tooltip title={a.dataType} arrow followCursor enterDelay={300} placement="bottom">
        <div>{a.name}</div>
      </Tooltip>
    </div>
  );
};
